import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { PageAccesses } from '../../../core/guards/page-accesses.model';
import { PageAccessesService } from '../../../core/services/page-access/page-access.service';
import { TrayFormViewModelType } from '../../../create-tray-screen/components/view-model-common/view-model-type';
import { ConfirmDialogService } from '../../../shared/components/confirm-dialog/confirm-dialog.service';
import { TrayCloningDialogService } from '../../../shared/components/tray-cloning-dialog/tray-cloning-dialog.service';
import { TableViewComponent } from '../../../shared/components/table-view/table-view.component';
import { deepEqual } from '../../../shared/functions/deepEqual';
import { PageType } from '../../page-type';
import { TrayTypeBridge } from '../../tray-type-bridge';
import { PageEditTrayItemViewModel, PageEditTrayLayout, PageEditTrayUserSegment } from '../page-edit-screen/page-edit-screen.view-model';
import { trayListLayoutAssets } from './tray-list-layout-assets';
import { TrayListService } from './tray-list.service';
import { AdminUiDetailsService } from '../../../admin-ui-details/admin-ui-details.service';
import { AdminUiPageActions } from '../../../domain/admin-ui-details';
import { CreateTrayScreenNavigationService } from '../../../create-tray-screen/components/create-tray-screen-navigation/create-tray-screen-navigation.service';
import { CreateTrayScreenService } from '../../../create-tray-screen/components/create-tray-screen/create-tray-screen.service';

@Component({
  selector: 'osn-cxb-tray-list',
  templateUrl: './tray-list.component.html',
  styleUrls: ['./tray-list.component.scss'],
  providers: [TrayListService, CreateTrayScreenNavigationService, CreateTrayScreenService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrayListComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input() public vm: PageEditTrayItemViewModel[];
  @Input() public pageVersionId: string;
  @Input() public userSegmentId: string;
  @Input() public countryId: string;
  @Input() public readonly: boolean;
  @Input() public pageType: PageType;
  @Input() public pageId: string;
  @Input() public showScheduling = true;
  @Input() public showCountries = true;
  @Input() public showUserSegments = true;
  @Input() public showContentSource = false;
  @Input() public showLayout = true;
  @Input() public showTrayType = true;
  @Input() public showPlatformType = true;
  @ViewChild(TableViewComponent) private readonly table: TableViewComponent<PageEditTrayItemViewModel>;
  public readonly PageEditTrayUserSegment = PageEditTrayUserSegment;
  public readonly PageEditTrayLayout = PageEditTrayLayout;
  public readonly TrayFormViewModelType = TrayFormViewModelType;
  private readonly destroyed$ = new Subject<void>();
  public pageActions$: Observable<AdminUiPageActions>;

  public readonly onRowClick = (event: MouseEvent, tray: PageEditTrayItemViewModel): void => {
    if (this.accesses.screen.pageDetailScreen.openTray) {
      this.navigateToTrayItem(tray);
    }
  };

  public readonly onTrayEditClick = (tray: PageEditTrayItemViewModel): void => {
    this.navigateToTrayItem(tray);
  };

  private readonly layoutAssets = trayListLayoutAssets;

  public accesses: PageAccesses;

  constructor(
    private readonly router: Router,
    private readonly trayListService: TrayListService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly trayCloningDialogService: TrayCloningDialogService,
    private readonly pageAccessesService: PageAccessesService, // private readonly trayCloningDialogService: TrayCloningDialogService,
    private readonly adminUiDetailsService: AdminUiDetailsService,
    private readonly createTrayScreenNavigationService: CreateTrayScreenNavigationService
  ) {
    this.accesses = this.pageAccessesService.usersPageAccesses$.value;
  }

  public darkRow(tray: PageEditTrayItemViewModel): boolean {
    return !tray.visible;
  }

  public ngAfterViewInit(): void {
    // eslint-disable-next-line import/no-deprecated
    this.table.dataSource$.pipe(takeUntil(this.destroyed$), skip(1)).subscribe((trays: PageEditTrayItemViewModel[]) => {
      if (!deepEqual(this.vm, trays)) {
        this.vm = [...trays];
        this.trayListService.updateTraysOrder(this.pageVersionId, trays, this.pageType);
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private navigateToTrayItem(tray: PageEditTrayItemViewModel) {
    if (this.createTrayScreenNavigationService.isTrayRefactored(tray.trayType)) {
      this.createTrayScreenNavigationService.navigateToEditTray(tray);
    } else {
      let subRoute = '';
      if (tray.trayType === TrayFormViewModelType.AppsAndGames) {
        subRoute = TrayTypeBridge.parseFromPageItemType(tray['subType']).subRoute;
      } else {
        subRoute = TrayTypeBridge.parseFromFormType(tray.trayType).subRoute;
      }
      this.router.navigate([this.pageType.routePath, this.pageVersionId, subRoute, tray.id]);
    }
  }

  public onTrayVisibleChangeClick(trayId: string): void {
    const trays = [...this.vm.map((tray) => ({ ...tray }))];
    const index = trays.findIndex((tray) => tray.id === trayId);
    trays[index].visible = !trays[index].visible;
    this.vm = trays;
    this.trayListService.updateTraysVisible(this.pageVersionId, [this.vm[index]], this.pageType);
  }

  public onTrayCloneClick(tray: PageEditTrayItemViewModel): void {
    this.trayCloningDialogService
      .openDialog({
        title: 'Select the page where you want to clone the tray',
        cancelBtnLabel: 'Cancel',
        confirmBtnLabel: 'Clone this tray',
        pageType: this.pageType,
        pageId: this.pageId,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result?: string) => {
        if (!result) {
          return;
        }
        this.trayListService.cloneTray(this.pageVersionId, tray.id, tray.trayType, this.pageType, result);
      });
  }

  public onTrayDeleteClick(tray: PageEditTrayItemViewModel): void {
    this.confirmDialogService
      .openDialog({
        title: 'Are you sure you want to delete this tray?',
        cancelBtnLabel: 'Keep this tray',
        confirmBtnLabel: 'Delete this tray',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      // eslint-disable-next-line import/no-deprecated, complexity
      .subscribe((result: boolean) => {
        if (!result) {
          return;
        }
        this.trayListService.deleteTray(this.pageVersionId, tray, this.pageType);
      });
  }

  public castToTray(tray: any): PageEditTrayItemViewModel {
    return tray;
  }

  public getLayoutData(layout: PageEditTrayLayout): { src: string; alt: string } {
    return this.layoutAssets[layout] || this.layoutAssets[PageEditTrayLayout.Landscape];
  }

  public onMenuClick(event: MouseEvent) {
    event.stopPropagation();
  }

  public ngOnInit(): void {
    this.pageActions$ = this.adminUiDetailsService.getPageActionsByType$(this.pageType);
  }
}
